/**
 * API configuration and utilities
 */

// Get API base URL from environment variables
export const getApiBaseUrl = (): string => {
  return import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002';
};

// Create full API URL
export const createApiUrl = (endpoint: string): string => {
  const baseUrl = getApiBaseUrl();
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${cleanEndpoint}`;
};

// Common API endpoints
export const API_ENDPOINTS = {
  DEVICE_ACTIVATE_INITIATE: '/api/auth/device/initiate',
  DEVICE_ACTIVATE_STATUS: '/api/auth/device/status',
  DEVICE_ACTIVATE: '/api/auth/device/activate',
  // TMDB endpoints
  TMDB_TRENDING: '/api/tmdb/trending',
  TMDB_POPULAR: '/api/tmdb/popular',
  TMDB_TOP_RATED: '/api/tmdb/top-rated',
  TMDB_SEARCH: '/api/tmdb/search-tmdb',
  TMDB_SEARCH_STREAM: '/api/tmdb/search-tmdb-stream',
  TMDB_SEARCH_SUGGESTIONS: '/api/tmdb/search-suggestions',
  TMDB_MOVIE_DETAILS: '/api/tmdb/movie',
  TMDB_VIDEOS: '/api/tmdb/videos',
  TMDB_SIMILAR: '/api/tmdb/similar',
  
  // MDBList endpoints
  MDBLIST_LISTS: '/api/mdblist/lists',
  MDBLIST_CATEGORIES_LANGUAGES: '/api/mdblist/categories/languages',
  MDBLIST_CATEGORIES_GENRES: '/api/mdblist/categories/genres',
  MDBLIST_SEARCH: '/api/mdblist/search',
  
  // Streaming endpoints
  SEARCH: '/api/search',
  STREAM_MOVIE: '/api/stream-movie',
  STREAM_DRIVE_OAUTH: '/api/stream-drive-oauth',
  STREAM_DRIVE_DIRECT: '/api/stream-drive-direct',
  PROXY_FILESTREAM: '/api/proxy-filestream',
  VIDSRC_CHECK: '/api/vidsrc/check',
  
  // Upload endpoints
  UPLOAD_STATUS: '/api/upload-status',
  UPLOAD_PROGRESS: '/api/upload-progress',
  
  // Admin endpoints
  ADMIN_UPLOAD_JOBS: '/api/admin/upload-jobs',
  ADMIN_CACHE_STATS: '/api/admin/cache/stats',
  ADMIN_CACHE_CLEAR: '/api/admin/cache/clear',
  ADMIN_CACHE_SYNC: '/api/admin/sync-cache',
  ADMIN_TEST_GDRIVE: '/api/admin/test-gdrive',
  
  // Auth endpoints
  AUTH_USER_INFO: '/api/auth/user-info',
  AUTH_SIGNOUT: '/api/auth/signout',
  
  // Watch History endpoints
  WATCH_HISTORY_RECENT: '/api/watch-history/recent',
  WATCH_HISTORY_RECENT_GROUPED: '/api/watch-history/recent-grouped',
  WATCH_HISTORY_TRACK: '/api/watch-history/track',
  WATCH_HISTORY_COMPLETE: '/api/watch-history/mark-complete',
  WATCH_HISTORY_REMOVE: '/api/watch-history',
  WATCH_HISTORY_CHECK_UPGRADE: '/api/watch-history/check-upgrade',
  WATCH_HISTORY_UPGRADE_TO_GDRIVE: '/api/watch-history/upgrade-to-gdrive',
} as const;

// Helper function to create API URLs
export const getApiUrl = (endpoint: keyof typeof API_ENDPOINTS, pathParam?: string, params?: Record<string, string>): string => {
  let url = createApiUrl(API_ENDPOINTS[endpoint]);
  
  // Add path parameter if provided (e.g., for /watch-history/:tmdbId)
  if (pathParam) {
    url += `/${pathParam}`;
  }
  
  if (params) {
    const searchParams = new URLSearchParams(params);
    url += `?${searchParams.toString()}`;
  }
  
  return url;
};

// Helper function for MDBList individual lists
export const getMDBListUrl = (listId: string): string => {
  const baseUrl = getApiBaseUrl();
  return `${baseUrl}/api/mdblist/list/${listId}`;
};
// Deployment trigger - Tue Oct 28 22:04:46 +04 2025
